import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useRouter } from 'next/router';
import { FilterX, Plus } from 'lucide-react';
import type { WebGoal } from '../../../lib/goals';
import { createGoalContext, findGoal, newGoalSeedKey, useWorkspaceGoals } from '../../../lib/goals';
import { useWorkspaces } from '../../../lib/workspaces';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useLoadBi } from '../../../lib/use-load-bi';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { useTranslation } from '../../../lib/i18n';
import { offersAChoice, spacesOf, type FilterOption } from '../../../lib/task-filters';
import { useTaskSidePanel } from '../task-side-panel/TaskSidePanelContext';
import { Button, OverflowMenu, type OverflowMenuAction } from '../ui';
import { GoalCard } from './GoalCard';
import { LiveGoalDetailsModal } from './GoalDetailsModal';
import styles from './GoalsBody.module.css';

// THE goals renderer — grid + create CTA, self-contained (fetches its own data). Mirrors `TasksBody`'s
// two scoping modes so the same component serves the top-level Goals screen AND a Space's Dashboard tile
// drill-in:
//   - ACCOUNT-WIDE (no workspaceId, or `filterable` with none selected) — the top-level `/goals` screen.
//   - PER-WORKSPACE (workspaceId, no `filterable`) — a host that has already fixed its scope.
//
// `filterable` adds a Space picker (`TasksFilterBar`'s single-axis sibling) and changes what `workspaceId`
// MEANS: with it, the list is always fetched account-wide and `workspaceId` is the SELECTED Space (read off
// `/goals?workspace=<slug>` by the page), so a reader can widen back to every Space. Without it,
// `workspaceId` is a host scope applied server-side, exactly as the original workspace Goals tab did.
export function GoalsBody({
  workspaceId,
  header,
  filterable,
  page = 'goals',
}: {
  workspaceId?: string;
  header?: ReactNode;
  filterable?: boolean;
  page?: string;
}) {
  const { t } = useTranslation();
  const { openModal } = useTaskSidePanel();
  const { logPageClickBi } = useGenericBi();
  const openSideChat = useOpenSideChat();
  const router = useRouter();
  const workspacesState = useWorkspaces();
  const fullRoster = workspacesState.status === 'ready' ? workspacesState.workspaces : [];
  const workspaceRoster: FilterOption[] = fullRoster;

  const state = useWorkspaceGoals(filterable ? undefined : workspaceId);
  useLoadBi('goals-data', { isLoading: state.status === 'loading', isError: state.status === 'error' });

  const spaceOptions = filterable && state.status === 'ready' ? spacesOf(state.goals, workspaceRoster) : [];
  const selectedId = filterable ? workspaceId ?? '' : '';
  const goals = filterable && state.status === 'ready' ? state.goals.filter((goal) => !selectedId || goal.workspaceId === selectedId) : state.status === 'ready' ? state.goals : [];
  // The Space actually in scope for the create-goal seed: the FILTER pick when filterable, else the
  // host's own fixed `workspaceId` — never `selectedId`, which is always '' in the non-filterable mode.
  const scopeId = filterable ? selectedId : workspaceId;
  const selectedName = workspaceRoster.find((w) => w.id === scopeId)?.name;

  const applyFilter = (nextId: string) => {
    logPageClickBi({ page_name: page, button_name: nextId ? 'filter_space' : 'filter_space_all' });
    const slug = fullRoster.find((w) => w.id === nextId)?.slug;
    const query = { ...router.query };
    if (slug) query.workspace = slug;
    else delete query.workspace;
    void router.push({ pathname: router.pathname, query }, undefined, { shallow: true });
  };

  const deepLinkedGoalId = typeof router.query.goal === 'string' ? router.query.goal : '';
  const openedDeepLink = useRef('');

  const openGoal = (goal: WebGoal) => {
    logPageClickBi({ page_name: page, button_name: 'open_goal', answer: goal.id });
    openModal(<LiveGoalDetailsModal workspaceId={goal.workspaceId} goalId={goal.id} initialGoal={goal} />, goal.id);
  };

  // `workspace/<slug>/goal/<goalId>` lands here as `?goal=<goalId>` (see lib/app-link.ts) — open that
  // goal once the list it lives in has loaded. Guarded on the id already opened rather than a bare
  // boolean so a second deep link in the same session still opens, while a re-render does not reopen
  // a modal the user has closed.
  useEffect(() => {
    if (!deepLinkedGoalId || state.status !== 'ready' || openedDeepLink.current === deepLinkedGoalId) {
      return;
    }
    const goal = findGoal(state.goals, deepLinkedGoalId);
    if (!goal) {
      return;
    }
    openedDeepLink.current = deepLinkedGoalId;
    openModal(<LiveGoalDetailsModal workspaceId={goal.workspaceId} goalId={goal.id} initialGoal={goal} />, goal.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkedGoalId, state]);

  // The empty state's "Create a goal" CTA: the unscoped seed, same key and copy as native's
  // GenericEmptyState CTA (iOS `GoalsTab.createGoal`, Android `GoalsList onCreateGoal`).
  const createGoal = () => {
    logPageClickBi({ page_name: page, button_name: 'create_goal' });
    openSideChat({ kind: 'new', message: t('goals.createPrompt') });
  };

  // The populated list's "+ New goal" affordance, scoped to the SELECTED Space when there is one (a host
  // scope, or a filter pick) — unscoped otherwise, matching the account-wide screen with nothing narrowed.
  const createGoalScoped = () => {
    logPageClickBi({ page_name: page, button_name: 'create_goal' });
    const name = (selectedName ?? '').trim();
    openSideChat({
      kind: 'new',
      message: t(newGoalSeedKey(scopeId ? name : undefined), { workspace: name }),
      context: createGoalContext(scopeId, name),
    });
  };

  return (
    <div className={styles.live}>
      {header}
      {state.status === 'error' && <div className={styles.error}>{t('goals.error')}</div>}
      {state.status === 'loading' && (
        <div className={styles.grid}>
          {[0, 1, 2].map((i) => (
            <div key={i} className={styles.skeleton} aria-hidden />
          ))}
        </div>
      )}
      {state.status === 'ready' &&
        (goals.length === 0 ? (
          <div className={styles.empty}>
            <div className={styles.emptyTitle}>{t('goals.empty.title')}</div>
            <div className={styles.emptyBody}>{t('goals.empty.body')}</div>
            <div className={styles.emptyCta}>
              <Button onClick={createGoal}>{t('goals.empty.cta')}</Button>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.actions}>
              {filterable && offersAChoice(spaceOptions) && (
                <SpaceFilter options={spaceOptions} selectedId={selectedId} onChange={applyFilter} />
              )}
              <Button size="small" startIcon={<Plus size={16} aria-hidden />} onClick={createGoalScoped}>
                {t('goals.create.cta')}
              </Button>
            </div>
            <div className={styles.grid}>
              {goals.map((goal) => (
                <GoalCard key={goal.id} goal={goal} onOpen={() => openGoal(goal)} />
              ))}
            </div>
          </>
        ))}
    </div>
  );
}

function SpaceFilter({ options, selectedId, onChange }: { options: FilterOption[]; selectedId: string; onChange: (id: string) => void }) {
  const { t } = useTranslation();
  const label = options.find((o) => o.id === selectedId)?.name ?? t('tasks.filter.allSpaces');
  const actions: OverflowMenuAction[] = [
    { key: 'all', label: t('tasks.filter.allSpaces'), onSelect: () => onChange('') },
    ...options.map((option) => ({ key: option.id, label: option.name, onSelect: () => onChange(option.id) })),
  ];
  return (
    <div className={styles.filters}>
      <OverflowMenu label={label} ariaLabel={t('packs.section.workspace')} actions={actions} selectedKey={selectedId || 'all'} />
      {selectedId && (
        <Button variant="text" size="small" startIcon={<FilterX size={16} aria-hidden />} onClick={() => onChange('')}>
          {t('files.filter.clear')}
        </Button>
      )}
    </div>
  );
}
